import { prototypeRuntimeConfig } from "../prototypeRuntimeConfig";
import { inputValue, matchLastQuotedArg } from "../domUtils";
import { describeAuthError } from "./authErrorMessages";
import { PrototypeApiError } from "../apiClient";
import type { PrototypeRuntime, Root } from "../prototypeTypes";
import { isOptionalIntegrationError } from "./integrationErrors";
import { resetLoginTurnstileAfterError, resetRegisterTurnstileAfterError } from "../turnstileWidgetController";
import { bindPendingCheckoutIntentToUser } from '../pendingCheckoutIntent';
import { bindPendingRedeemIntentToUser } from '../pendingRedeemIntent';
import { showEmailVerificationPendingContext } from './emailVerificationResendAction';
import { clearAuthModalFields } from '../formProfileController';
import { CURRENT_TERMS_VERSION } from '../../../domain/identityInputPolicy';
import { parseCanonicalAuthenticatedSession, type CanonicalAuthenticatedSession } from '../authenticatedSessionContract';

export function isAuthenticationAction(action: string) {
  return (
    action.includes("handleLogin") ||
    action.includes("handleRegister") ||
    action.includes("handleSocialLogin")
  );
}

/**
 * Result of a single auth attempt. `success` is the only path that may
 * promote the user to the logged-in state. `oauthRedirected` means the
 * page is about to leave for the provider's authorization URL — the
 * caller must NOT close the modal or flip the avatar. `failure`
 * carries a human-readable reason for the toast.
 */
type AuthOutcome =
  | { success: true }
  | { oauthRedirected: true }
  | { verificationNeeded: true }
  | { failure: string; silent?: boolean };

/**
 * Centralised auth orchestrator. The previous implementation called
 * `getSession()` + `hideLoginState()` + `closeAllModals()` after the
 * endpoint returned, regardless of whether the endpoint actually
 * succeeded. That meant a 503 from /api/auth/google/start was
 * caught by `isOptionalIntegrationError`, swallowed, and the user was
 * promoted to the logged-in UI anyway — which is a real security/UX
 * bug (avatar showed initials, gated tabs became clickable) and
 * exactly what the user reported in their last round.
 *
 * The new flow returns a discriminated `AuthOutcome` so the caller
 * (the action router) decides whether to flip the login state. The
 * function still throws on programmer errors, but any user-facing
 * failure path is encoded in `failure` and surfaced as a toast.
 */
export async function authenticate(
  root: Root,
  runtime: PrototypeRuntime,
  action: string,
  handlers: {
    hideLoginState(root: Root): void;
    closeAllModals(root: Root): void;
    failValidation(message: string): false;
    recordRuntimeError(runtime: PrototypeRuntime, error: unknown): void;
    onAuthSuccess?(session: CanonicalAuthenticatedSession): void;
    /** Called right before the network/turnstile round-trip begins. */
    onSubmitStart?(): void;
    /** Called after the round-trip settles (success, failure, or OAuth redirect). */
    onSubmitEnd?(): void;
	/** Rejects stale UI writes after a modal closes or a newer attempt begins. */
	isAttemptCurrent?(): boolean;
	/** Requires both current ownership and a still-active form surface for UI writes. */
	canWriteUi?(): boolean;
  },
): Promise<void> {
	const isAttemptCurrent = () => handlers.isAttemptCurrent?.() !== false;
	const canWriteUi = () => handlers.canWriteUi?.() !== false;
  let outcome: AuthOutcome;
  handlers.onSubmitStart?.();
	if (!isAttemptCurrent()) return;
  try {
    const actionKey = action.includes("Register") ? "auth-register" : "auth-login";
    // Social login uses the development token, no visible widget needed.
    // For email/password flows, check that the visible Turnstile widget has
    // been solved before attempting to get the token.
    if (!action.includes("SocialLogin") && !runtime.interactiveTurnstile?.hasToken()) {
      throw new Error("turnstile_not_solved");
    }
    const turnstileToken = action.includes("SocialLogin")
      ? prototypeRuntimeConfig.developmentTurnstileToken
      : await runtime.interactiveTurnstile!.getToken(actionKey);
		if (!canWriteUi()) return;
    outcome = await callAuthEndpoint(root, runtime, action, turnstileToken, handlers.recordRuntimeError, isAttemptCurrent, canWriteUi);
  } catch (error) {
		if (!canWriteUi()) return;
    if (error instanceof Error && error.message === "turnstile_not_solved") {
      outcome = { failure: "Please complete the security verification first." };
    } else {
      handlers.recordRuntimeError(runtime, error);
      outcome = { failure: describeAuthError(error, action.includes("Register") ? "register" : "login") };
    }
  } finally {
		if (canWriteUi()) handlers.onSubmitEnd?.();
  }
	if (!isAttemptCurrent()) return;
  if ("success" in outcome) {
    // Pull the canonical session and let the controller upgrade
    // state.currentPlan / userId / userEmail BEFORE we flip the
    // logged-in UI. If we did this in the wrong order the home view
    // would briefly render with state.currentPlan === "free" (the
    // visitor default) — the user reported exactly this regression:
    // "logged in as Max but home still shows the Free badge".
    try {
		const session = parseCanonicalAuthenticatedSession(await runtime.api.getSession());
		if (!isAttemptCurrent()) return;
		if (!session) throw new Error('canonical_authenticated_session_incomplete');
		handlers.onAuthSuccess?.(session);
    } catch (error) {
		if (!isAttemptCurrent()) return;
      handlers.recordRuntimeError(runtime, error);
		if (canWriteUi()) handlers.failValidation("You're signed in, but we couldn't load your account. Refresh the page to continue.");
		return;
    }
	if (!isAttemptCurrent()) return;
    handlers.hideLoginState(root);
	if (action.includes('handleLogin')) clearAuthModalFields(root, 'login');
    handlers.closeAllModals(root);
    return;
  }
  if ("oauthRedirected" in outcome) {
    // Page is leaving; do not touch UI state.
    return;
  }
  if ("verificationNeeded" in outcome) {
    // Registration succeeded but email is unverified. The success view
    // has already been shown by callAuthEndpoint. Do NOT fetch session,
    // do NOT flip login state, do NOT close the modal.
    return;
  }
  if (!outcome.silent) handlers.failValidation(outcome.failure);
}


async function callAuthEndpoint(
  root: Root,
  runtime: PrototypeRuntime,
  action: string,
	turnstileToken: string,
	recordRuntimeError: (runtime: PrototypeRuntime, error: unknown) => void,
	isAttemptCurrent: () => boolean,
	canWriteUi: () => boolean,
): Promise<AuthOutcome> {
  // OAuth: never use `isOptionalIntegrationError` to swallow. A 503
  // from the OAuth start endpoint means the provider credentials
  // aren't configured server-side. We must surface that to the user
  // AND we must not let the outer caller promote them to logged-in.
  if (action.includes("handleSocialLogin")) {
    const provider = (matchLastQuotedArg(action, "handleSocialLogin") ??
      "google") as "google";
    try {
	  // The legal disclosure immediately beneath each auth submit button makes
	  // this explicit, click-based consent. Carry it in signed OAuth state for
	  // both entry points so first-time Google users can be created in one pass.
      const startResponse = (await runtime.api.startOAuth(provider, { acceptedTerms: true, termsVersion: CURRENT_TERMS_VERSION })) as {
        authorizationUrl?: string;
      };
		if (!canWriteUi()) return { failure: '', silent: true };
      if (startResponse?.authorizationUrl) {
        window.location.assign(startResponse.authorizationUrl);
        return { oauthRedirected: true };
      }
      // Server returned 2xx but no URL — treat as a server bug.
      recordRuntimeError(runtime, new Error("OAuth start returned no authorizationUrl"));
      return {
        failure: "Google sign-in is not configured on this environment yet. Use email + password.",
      };
    } catch (error) {
		if (!canWriteUi()) return { failure: '', silent: true };
      recordRuntimeError(runtime, error);
      // Surface a useful toast, but signal that the caller must NOT
      // promote the user to logged-in state.
      if (error instanceof PrototypeApiError && error.status === 503) {
        return {
          failure: "Google sign-in is not configured on this environment yet. Use email + password.",
        };
      }
      if (error instanceof PrototypeApiError && error.status === 404) {
        return {
          failure: "Google sign-in is unavailable right now. Use email + password.",
        };
      }
      return {
        failure: "Google sign-in failed. Use email + password.",
      };
    }
  }
  try {
    if (action.includes("handleRegister")) {
		const email = inputValue(root, "regEmail").trim();
		const registration = await runtime.api.register({
        name: inputValue(root, "regName").trim(),
		email,
        password: inputValue(root, "regPassword"),
		acceptedTerms: true,
		termsVersion: CURRENT_TERMS_VERSION,
        turnstileToken,
		});
		if (!isAttemptCurrent()) return { failure: '', silent: true };
		if (registration?.viewer?.userId) {
			bindPendingCheckoutIntentToUser(registration.viewer.userId);
			bindPendingRedeemIntentToUser(registration.viewer.userId);
		}
		if (!canWriteUi()) return { failure: '', silent: true };
		await showEmailVerificationPendingContext(root, runtime, email);
		if (!canWriteUi()) return { failure: '', silent: true };
		clearAuthModalFields(root, 'register');
      return { verificationNeeded: true };
    }
    if (action.includes("handleLogin")) {
      await runtime.api.login({
        email: inputValue(root, "loginEmail").trim(),
        password: inputValue(root, "loginPassword"),
        turnstileToken,
      });
		if (!isAttemptCurrent()) return { failure: '', silent: true };
      return { success: true };
    }
  } catch (error) {
	if (!canWriteUi()) return { failure: '', silent: true };
	if (action.includes('Register') && error instanceof PrototypeApiError && error.status === 409) {
		const body = error.body as Record<string, unknown> | undefined;
		if (body?.error === 'email_already_registered') {
			const email = inputValue(root, 'regEmail').trim();
			await showEmailVerificationPendingContext(root, runtime, email, 'existing_account', body.verificationPending === true);
			return { verificationNeeded: true };
		}
	}
	if (error instanceof PrototypeApiError && error.status === 403 && !action.includes('Register')) {
		const code = typeof (error.body as Record<string, unknown> | undefined)?.error === 'string'
			? (error.body as Record<string, string>).error
			: '';
		if (code === 'email_not_verified') {
			const email = inputValue(root, 'loginEmail').trim();
			await showEmailVerificationPendingContext(root, runtime, email, 'login_rejected', true);
			return { verificationNeeded: true };
		}
	}
    // Every email/password attempt consumes its one-shot Turnstile token,
    // including valid challenges followed by bad credentials or another
    // recoverable server error. Reset the visible widget before surfacing the
    // failure so the next submit can obtain a fresh token.
    if (action.includes("Register")) {
      resetRegisterTurnstileAfterError(root, runtime);
    } else {
      resetLoginTurnstileAfterError(root, runtime);
    }
    if (isOptionalIntegrationError(error)) {
      // The auth endpoint itself is optional (e.g. social login
      // misconfigured on a fully-on-prem build). Swallow but DO NOT
      // mark the user as logged in.
      recordRuntimeError(runtime, error);
      return { failure: "Sign-in service is not configured on this environment. Use email + password.", silent: false };
    }
    throw error;
  }
  return { failure: "Unknown sign-in action." };
}
